/**
 * Dashboard variables: where a tile's ship and date range actually come from.
 *
 * v2 answered that with two booleans on every tile - `bindContext` plus a
 * `context` object - and a toolbar wired to the same atoms as Explore, so the
 * ship you were looking at followed you into the dashboard. The default is
 * right. The mechanism could not grow: "this dashboard always shows the
 * Aurora", "slice every tile by voyage leg" and "one row per selected ship" all
 * need a *named value* that tiles reference, not a boolean per tile.
 *
 * So there is one concept instead, which is Grafana's `templating.list` under a
 * plainer name. A dashboard declares variables; a tile either references one
 * (`"$ship"`) or pins a literal. Two are built in - `ship` and `window` - and
 * everything the app does today is those two with `binding: 'url'`, which is
 * exactly the old behaviour spelled out.
 *
 * `binding` is what makes a fixed dashboard possible without a second mechanism:
 *
 * * `url` - seed from the shared ship/date atoms and the launch URL, as today.
 * * `fixed` - the dashboard owns the value and the URL cannot move it.
 * * `lastUsed` - per viewer, remembered between visits. Resolves like `fixed`
 *   until there is somewhere to remember it (`docs/plans/dashboards.md`, deferred).
 *
 * Nothing here reads an atom. The caller passes what the URL and Explore
 * currently say, so this stays pure and testable, and so a tile can be resolved
 * for any set of values - which is what row repeats will need.
 */

import { presetWindow } from '../../utils/dates.ts';

export const SHIP_VAR = 'ship';
export const WINDOW_VAR = 'window';
/**
 * The window Live tiles draw. A source without history wants minutes or hours
 * where EOS wants days or months, so a mixed dashboard has two windows: a Live
 * tile that follows the dashboard's time follows this one instead of
 * `window`. Stored with the dashboard, by preset name so it keeps rolling; a
 * dashboard saved before it existed gets `DEFAULT_LIVE_WINDOW`.
 */
export const LIVE_WINDOW_VAR = 'live';
export const DEFAULT_LIVE_WINDOW: Readonly<TimeWindow> = { preset: '10m' };

export type VariableBinding = 'url' | 'fixed' | 'lastUsed';
export type VariableType = 'ship' | 'timeRange' | 'query' | 'custom';

/** A window as stored: a preset name, or an explicit pair. */
export interface TimeWindow {
  preset?: string;
  start?: string;
  end?: string;
}

export type VariableValue = string[] | TimeWindow | null;

export interface DashboardVariable {
  id: string;
  type: VariableType;
  label: string;
  binding: VariableBinding;
  /** Ship variables only: whether more than one may be selected, and how many. */
  multi?: boolean;
  max?: number;
  /** The value when nothing else supplies one. Shape follows `type`. */
  default: VariableValue;
  /** Last resolved value. Stored for `fixed` and `lastUsed`; ignored for `url`. */
  current?: VariableValue;
  /**
   * Where a `query` variable's options come from - a backend tool and its
   * arguments, which may themselves reference variables. Inert today; the field
   * exists so adding voyage-leg slicers is not a document migration.
   */
  source?: { tool: string; args?: Record<string, unknown> } | null;
}

/**
 * How a slicer offers its column. `list` and `dropdown` pick discrete values;
 * `range` is a two-handle slider over a number; `time` the same over a time
 * column, in dates.
 */
export type SlicerMode = 'list' | 'dropdown' | 'range' | 'time';

/** What kind of column a slicer sits on, which decides the modes it may use. */
export type SlicerFamily = 'text' | 'int' | 'float' | 'time';

/** A slicer as stored on its tile: which column, and how it is shown. */
export interface SlicerDef {
  column: string;
  family: SlicerFamily;
  mode: SlicerMode;
}

/**
 * Which kind of column each way of showing a slicer takes. Lists and dropdowns
 * take any column but a time (checked for having few enough values when made),
 * a range a number, dates a time column.
 */
export function modeFits(mode: SlicerMode, family: SlicerFamily): boolean {
  switch (mode) {
    case 'list':
    case 'dropdown':
      return family !== 'time';
    case 'range':
      return family === 'int' || family === 'float';
    case 'time':
      return family === 'time';
  }
}

/**
 * A slicer that narrows something - what a tile's request is cut by. Either
 * `values` (keep readings whose column is one of them) or a `min`/`max` window,
 * in the column's own units (epoch seconds when `temporal`).
 */
export interface ActiveSlicer {
  id: string;
  column: string;
  values?: string[];
  min?: number | null;
  max?: number | null;
  temporal?: boolean;
}

/** A reference to a variable, as stored on a tile: `"$ship"`. */
export type VariableRef = `$${string}`;

export function isRef(value: unknown): value is VariableRef {
  return typeof value === 'string' && value.startsWith('$') && value.length > 1;
}

export function refName(ref: VariableRef): string {
  return ref.slice(1);
}

/**
 * The two every dashboard has. Both are the dashboard's own (`fixed`): since
 * 2026-10-05 no dashboard follows Explore. One that has no value yet is given
 * Explore's selection once, when it opens (`adoptExploreSelection`).
 */
export function defaultVariables(): DashboardVariable[] {
  return [
    {
      id: SHIP_VAR,
      type: 'ship',
      label: 'Ship',
      binding: 'fixed',
      multi: true,
      default: [],
    },
    {
      id: WINDOW_VAR,
      type: 'timeRange',
      label: 'Range',
      binding: 'fixed',
      default: {},
    },
  ];
}

/** Built-ins for a newly created dashboard that owns its initial selection. */
export function defaultDetachedVariables(
  ships: string[],
  window: TimeWindow,
): DashboardVariable[] {
  return defaultVariables().map((variable) => ({
    ...variable,
    binding: 'fixed',
    current: variable.id === SHIP_VAR ? [...ships] : { ...window },
  }));
}

/** What the URL and Explore currently say - the `url` binding's source. */
export interface ContextFallback {
  ships: string[];
  start: string;
  end: string;
}

/** Resolved values, with nothing left to look up. */
export interface VariableValues extends ContextFallback {
  /** Every variable's resolved value, for tiles that reference one by name. */
  byId: Record<string, VariableValue>;
  /**
   * The open tab's slicer selections, added by the view that knows the tab and
   * the viewer. Absent means nothing is sliced.
   */
  slicers?: ActiveSlicer[];
}

/** A stored window as a concrete pair, or null when it cannot be resolved. */
export function resolveWindow(window: TimeWindow | undefined | null): { start: string; end: string } | null {
  if (!window) return null;
  // A preset means the same thing here as in the sidebar - `max` deliberately
  // resolves to null, because "this ship's whole range" is a metadata lookup
  // rather than arithmetic, and the caller falls back rather than inventing one.
  if (window.preset && window.preset !== 'custom') {
    const resolved = presetWindow(window.preset);
    if (resolved) return resolved;
  }
  if (window.start && window.end) return { start: window.start, end: window.end };
  return null;
}

function shipValue(value: VariableValue): string[] {
  return Array.isArray(value) ? value.filter((s) => typeof s === 'string' && s) : [];
}

function windowValue(value: VariableValue): TimeWindow | null {
  return value && !Array.isArray(value) ? value : null;
}

/**
 * Resolve every variable against what the URL says.
 *
 * A `url` variable is the fallback, whatever it holds - that is the binding's
 * meaning, and it is why opening a dashboard from Explore keeps your ship. A
 * `fixed` one prefers its own value and only falls back when it has none, so a
 * half-configured dashboard still draws something rather than nothing.
 */
export function resolveVariables(
  variables: readonly DashboardVariable[],
  fallback: ContextFallback,
): VariableValues {
  const byId: Record<string, VariableValue> = {};
  let ships = fallback.ships;
  let start = fallback.start;
  let end = fallback.end;

  for (const variable of variables) {
    const own = variable.current ?? variable.default;
    const fromUrl = variable.binding === 'url';

    if (variable.type === 'ship') {
      const picked = fromUrl ? fallback.ships : shipValue(own);
      const value = picked.length ? picked : fallback.ships;
      byId[variable.id] = value;
      if (variable.id === SHIP_VAR) ships = value;
      continue;
    }

    if (variable.type === 'timeRange') {
      const stored = fromUrl ? null : windowValue(own);
      const resolved = resolveWindow(stored);
      const value: TimeWindow = resolved ?? { start: fallback.start, end: fallback.end };
      // The Live window keeps its preset's name, so a tile knows it rolls.
      byId[variable.id] =
        variable.id === LIVE_WINDOW_VAR && resolved && stored?.preset ? { ...value, preset: stored.preset } : value;
      if (variable.id === WINDOW_VAR) {
        start = value.start ?? fallback.start;
        end = value.end ?? fallback.end;
      }
      continue;
    }

    // `query` and `custom` variables have no consumer yet; carrying their value
    // through means a slicer added later needs a renderer, not a resolver.
    byId[variable.id] = own ?? null;
  }

  if (!(LIVE_WINDOW_VAR in byId)) {
    const value = resolveWindow(DEFAULT_LIVE_WINDOW);
    if (value) byId[LIVE_WINDOW_VAR] = { ...value, preset: DEFAULT_LIVE_WINDOW.preset! };
  }

  return { ships, start, end, byId };
}

/** The Live window as stored - a preset, usually - or the default. */
export function storedLiveWindow(variables: readonly DashboardVariable[]): TimeWindow {
  const v = variables.find((x) => x.id === LIVE_WINDOW_VAR);
  const value = v?.current ?? v?.default;
  return value && !Array.isArray(value) && (value.preset || (value.start && value.end))
    ? value
    : { ...DEFAULT_LIVE_WINDOW };
}

/** The `live` variable, for a dashboard about to store its first Live window. */
export function liveWindowVariable(): DashboardVariable {
  return {
    id: LIVE_WINDOW_VAR,
    type: 'timeRange',
    label: 'Live',
    binding: 'fixed',
    default: { ...DEFAULT_LIVE_WINDOW },
  };
}
